import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { verifyEmailToken } from '@/services/authApi';

type PageStatus = 'verifying' | 'success' | 'error';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<PageStatus>('verifying');
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get('token');
  const userId = searchParams.get('userId');

  useEffect(() => {
    const verify = async () => {
      if (!token || !userId) {
        setStatus('error');
        setError('Invalid verification link. Missing token or user.');
        return;
      }

      try {
        await verifyEmailToken({ token, userId });
        setStatus('success');
      } catch (err) {
        setStatus('error');
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Email verification failed. The link may be expired or invalid.');
        }
      }
    };

    verify();
  }, [token, userId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        {status === 'verifying' && (
          <>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
              <CardTitle>Verifying your email</CardTitle>
              <CardDescription>
                Please wait while we verify your email link...
              </CardDescription>
            </CardHeader>
          </>
        )}

        {status === 'success' && (
          <>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
                <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle>Email verified</CardTitle>
              <CardDescription>
                Your email has been verified successfully. You can now sign in to your account.
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex justify-center">
              <Link to="/login">
                <Button>Login</Button>
              </Link>
            </CardFooter>
          </>
        )}

        {status === 'error' && (
          <>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <CardTitle>Verification failed</CardTitle>
              <CardDescription>
                {error ?? 'Something went wrong during verification.'}
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex justify-center">
              <Link to="/login">
                <Button variant="ghost">Back to Login</Button>
              </Link>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
}
